import React, { useEffect, useState } from "react";

import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import FormHelperText from "@material-ui/core/FormHelperText";
import Switch from "@material-ui/core/Switch";

import useSettings from "../../hooks/useSettings";
import { toast } from 'react-toastify';
import { makeStyles } from "@material-ui/core/styles";

import { TextField } from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import useCompanySettings from "../../hooks/useSettings/companySettings";

// Layout fiel ao mockup "Configurações A" (docs/design/aeros-dark/mockups/Telas
// Aeros Dark - Kanban, Campanhas e Configurações.dc.html): grid 3 colunas de
// cards com select, micro-label "Recursos" com grid 2 colunas de cards com
// toggle, e as demais seções (mensagens, LGPD, pagamentos) no mesmo padrão.
const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    grid3: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 14,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    grid2: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 10,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    grid2Wide: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 14,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    spanAll: {
      gridColumn: "1 / -1",
    },
    sectionLabel: {
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      margin: "24px 0 12px",
    },
    settingCard: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`,
      borderRadius: 14,
      padding: "16px 18px",
      display: "flex",
      flexDirection: "column",
      gap: 9,
      height: "100%",
      boxSizing: "border-box",
      minWidth: 0,
    },
    settingCardLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      lineHeight: 1.3,
    },
    settingCardField: {
      width: "100%",
      margin: 0,
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: theme.palette.aeros.sidebar,
        fontSize: 13,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.aeros.borderStrong,
      },
      "& .MuiSelect-root": {
        padding: "9px 12px",
      },
      "& .MuiSelect-icon": {
        color: theme.palette.text.secondary,
      },
      "& .MuiOutlinedInput-input": {
        padding: "9px 12px",
      },
      "& .MuiOutlinedInput-multiline": {
        padding: 0,
      },
    },
    settingCardHelper: {
      fontSize: 11,
      margin: 0,
      color: theme.palette.text.secondary,
    },
    toggleCard: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`,
      borderRadius: 12,
      padding: "13px 18px",
      display: "flex",
      alignItems: "center",
      gap: 14,
      minWidth: 0,
    },
    toggleCardBody: {
      flex: 1,
      minWidth: 0,
    },
    toggleCardLabel: {
      fontSize: 13.5,
      fontWeight: 600,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
    },
    toggleCardDesc: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginTop: 2,
      lineHeight: 1.35,
    },
    toggleSwitch: {
      flexShrink: 0,
      width: 36,
      height: 20,
      padding: 0,
      "& .MuiSwitch-switchBase": {
        padding: 2,
        "&.Mui-checked": {
          transform: "translateX(16px)",
        },
      },
      "& .MuiSwitch-thumb": {
        width: 16,
        height: 16,
        boxShadow: "none",
      },
      "& .MuiSwitch-track": {
        borderRadius: 99,
        opacity: 1,
      },
    },
  };
});

const SelectSettingCard = ({ classes, label, value, onChange, helperText, children, ...rest }) => (
  <div className={classes.settingCard}>
    <span className={classes.settingCardLabel}>{label}</span>
    <FormControl variant="outlined" size="small" className={classes.settingCardField}>
      <Select value={value} onChange={onChange} inputProps={{ "aria-label": label }} {...rest}>
        {children}
      </Select>
    </FormControl>
    {helperText ? (
      <FormHelperText className={classes.settingCardHelper}>{helperText}</FormHelperText>
    ) : null}
  </div>
);

const TextSettingCard = ({ classes, label, helperText, className, ...rest }) => (
  <div className={`${classes.settingCard} ${className || ""}`}>
    <span className={classes.settingCardLabel}>{label}</span>
    <TextField
      variant="outlined"
      size="small"
      className={classes.settingCardField}
      inputProps={{ "aria-label": label }}
      {...rest}
    />
    {helperText ? (
      <FormHelperText className={classes.settingCardHelper}>{helperText}</FormHelperText>
    ) : null}
  </div>
);

// Toggle para as opções binárias. `checked` já vem decidido pelo chamador
// (o valor bruto pode ser "enabled"/"disabled" ou boolean, conforme a chave)
// e `onToggle(nextChecked)` devolve o próximo estado para o handler traduzir.
const ToggleSettingCard = ({ classes, label, desc, checked, onToggle, loading }) => (
  <div className={classes.toggleCard}>
    <div className={classes.toggleCardBody}>
      <div className={classes.toggleCardLabel}>{label}</div>
      <div className={classes.toggleCardDesc}>
        {loading ? i18n.t("settings.settings.options.updating") : desc}
      </div>
    </div>
    <Switch
      className={classes.toggleSwitch}
      checked={!!checked}
      onChange={(e) => onToggle(e.target.checked)}
      disabled={!!loading}
      inputProps={{ "aria-label": label }}
    />
  </div>
);

const SectionLabel = ({ classes, children }) => (
  <div className={classes.sectionLabel}>{children}</div>
);

const isOn = (v) => v === "enabled" || v === true;
const asFlag = (checked) => (checked ? "enabled" : "disabled");


export default function Options(props) {
  const { oldSettings, settings, scheduleTypeChanged, user } = props;

  const classes = useStyles();
  const [userRating, setUserRating] = useState("disabled");
  const [scheduleType, setScheduleType] = useState("disabled");
  const [chatBotType, setChatBotType] = useState("text");

  const [loadingUserRating, setLoadingUserRating] = useState(false);
  const [loadingScheduleType, setLoadingScheduleType] = useState(false);

  const [userCreation, setUserCreation] = useState("disabled");
  const [loadingUserCreation, setLoadingUserCreation] = useState(false);

  const [SendGreetingAccepted, setSendGreetingAccepted] = useState("enabled");
  const [loadingSendGreetingAccepted, setLoadingSendGreetingAccepted] = useState(false);

  const [UserRandom, setUserRandom] = useState("enabled");
  const [loadingUserRandom, setLoadingUserRandom] = useState(false);

  const [SettingsTransfTicket, setSettingsTransfTicket] = useState("enabled");
  const [loadingSettingsTransfTicket, setLoadingSettingsTransfTicket] = useState(false);

  const [AcceptCallWhatsapp, setAcceptCallWhatsapp] = useState("enabled");
  const [loadingAcceptCallWhatsapp, setLoadingAcceptCallWhatsapp] = useState(false);

  const [sendSignMessage, setSendSignMessage] = useState("enabled");
  const [loadingSendSignMessage, setLoadingSendSignMessage] = useState(false);

  const [sendGreetingMessageOneQueues, setSendGreetingMessageOneQueues] = useState("enabled");
  const [loadingSendGreetingMessageOneQueues, setLoadingSendGreetingMessageOneQueues] = useState(false);

  const [sendQueuePosition, setSendQueuePosition] = useState("enabled");
  const [loadingSendQueuePosition, setLoadingSendQueuePosition] = useState(false);

  const [sendFarewellWaitingTicket, setSendFarewellWaitingTicket] = useState("enabled");
  const [loadingSendFarewellWaitingTicket, setLoadingSendFarewellWaitingTicket] = useState(false);

  const [acceptAudioMessageContact, setAcceptAudioMessageContact] = useState("enabled");
  const [loadingAcceptAudioMessageContact, setLoadingAcceptAudioMessageContact] = useState(false);

  //PAYMENT METHODS
  const [eficlientidType, setEfiClientidType] = useState('');
  const [loadingEfiClientidType, setLoadingEfiClientidType] = useState(false);

  const [eficlientsecretType, setEfiClientsecretType] = useState('');
  const [loadingEfiClientsecretType, setLoadingEfiClientsecretType] =
    useState(false);

  const [efichavepixType, setEfiChavepixType] = useState('');
  const [loadingEfiChavepixType, setLoadingEfiChavepixType] = useState(false);

  const [mpaccesstokenType, setmpaccesstokenType] = useState('');
  const [loadingmpaccesstokenType, setLoadingmpaccesstokenType] =
    useState(false);

  const [mppublickeyType, setmppublickeyType] = useState('');
  const [loadingmppublickeyType, setLoadingmppublickeyType] = useState(false);

  const [pixdiscountpercentType, setpixdiscountpercentType] = useState('5');
  const [loadingpixdiscountpercentType, setLoadingpixdiscountpercentType] =
    useState(false);

  const [mpwebhooksecretType, setmpwebhooksecretType] = useState('');
  const [loadingmpwebhooksecretType, setLoadingmpwebhooksecretType] =
    useState(false);

  const [stripeprivatekeyType, setstripeprivatekeyType] = useState('');
  const [loadingstripeprivatekeyType, setLoadingstripeprivatekeyType] =
    useState(false);

  const [asaastokenType, setasaastokenType] = useState('');
  const [loadingasaastokenType, setLoadingasaastokenType] = useState(false);

  //OPENAI API KEY TRANSCRIÇÃO DE ÁUDIO
  const [openaitokenType, setopenaitokenType] = useState('');
  const [loadingopenaitokenType, setLoadingopenaitokenType] = useState(false);

  //LGPD
  const [enableLGPD, setEnableLGPD] = useState("disabled");
  const [loadingEnableLGPD, setLoadingEnableLGPD] = useState(false);

  const [lgpdMessage, setLGPDMessage] = useState("");
  const [loadinglgpdMessage, setLoadingLGPDMessage] = useState(false);

  const [lgpdLink, setLGPDLink] = useState("");
  const [loadingLGPDLink, setLoadingLGPDLink] = useState(false);

  const [lgpdDeleteMessage, setLGPDDeleteMessage] = useState("disabled");
  const [loadingLGPDDeleteMessage, setLoadingLGPDDeleteMessage] = useState(false);

  //LIMITAR DOWNLOAD
  // const [downloadLimit, setdownloadLimit] = useState("64");
  // const [loadingDownloadLimit, setLoadingdownloadLimit] = useState(false);

  const [lgpdConsent, setLGPDConsent] = useState("disabled");
  const [loadingLGPDConsent, setLoadingLGPDConsent] = useState(false);

  const [lgpdHideNumber, setLGPDHideNumber] = useState("disabled");
  const [loadingLGPDHideNumber, setLoadingLGPDHideNumber] = useState(false);

  // Tag obrigatoria
  const [requiredTag, setRequiredTag] = useState("enabled")
  const [loadingRequiredTag, setLoadingRequiredTag] = useState(false)

  // Fechar ticket ao transferir para outro setor
  const [closeTicketOnTransfer, setCloseTicketOnTransfer] = useState(false)
  const [loadingCloseTicketOnTransfer, setLoadingCloseTicketOnTransfer] = useState(false)

  // Usar carteira de clientes
  const [directTicketsToWallets, setDirectTicketsToWallets] = useState(false)
  const [loadingDirectTicketsToWallets, setLoadingDirectTicketsToWallets] = useState(false)

  // Sigla para inserir no copiar contatos
  const [copyContactPrefix, setCopyContactPrefix] = useState("");
const [loadingCopyContactPrefix, setLoadingCopyContactPrefix] = useState(false);

  //MENSAGENS CUSTOMIZADAS
  const [transferMessage, setTransferMessage] = useState("");
  const [loadingTransferMessage, setLoadingTransferMessage] = useState(false);

  const [greetingAcceptedMessage, setGreetingAcceptedMessage] = useState("");
  const [loadingGreetingAcceptedMessage, setLoadingGreetingAcceptedMessage] = useState(false);

  const [AcceptCallWhatsappMessage, setAcceptCallWhatsappMessage] = useState("");
  const [loadingAcceptCallWhatsappMessage, setLoadingAcceptCallWhatsappMessage] = useState(false);

  const [sendQueuePositionMessage, setSendQueuePositionMessage] = useState("");
  const [loadingSendQueuePositionMessage, setLoadingSendQueuePositionMessage] = useState(false);

  const [showNotificationPending, setShowNotificationPending] = useState(false);
  const [loadingShowNotificationPending, setLoadingShowNotificationPending] = useState(false);

  // 2FA Policy
  const [twoFactorPolicy, setTwoFactorPolicy] = useState("optional");
  const [loadingTwoFactorPolicy, setLoadingTwoFactorPolicy] = useState(false);

  const { update: updateUserCreation, getAll } = useSettings();

  // const { update: updatedownloadLimit } = useSettings();

  const { update: updateeficlientid } = useSettings();
  const { update: updateeficlientsecret } = useSettings();
  const { update: updateefichavepix } = useSettings();
  const { update: updatempaccesstoken } = useSettings();
  const { update: updatempwebhooksecret } = useSettings();
  const { update: updatestripeprivatekey } = useSettings();
  const { update: updateasaastoken } = useSettings();

  const { update } = useCompanySettings();

  const isSuper = () => {
    return user.super;
  };


  useEffect(() => {

    if (Array.isArray(oldSettings) && oldSettings.length) {

      const userPar = oldSettings.find((s) => s.key === "userCreation");

      if (userPar) {
        setUserCreation(userPar.value);
      }

      // const downloadLimit = oldSettings.find((s) => s.key === "downloadLimit");

      // if (downloadLimit) {
      //  setdownloadLimit(downloadLimit.value);
      // }

      const copyContactPrefix = oldSettings.find((s) => s.key === 'copyContactPrefix');
      if (copyContactPrefix) {
        setCopyContactPrefix(copyContactPrefix.value);
      }

      const eficlientidType = oldSettings.find((s) => s.key === 'eficlientid');
      if (eficlientidType) {
        setEfiClientidType(eficlientidType.value);
      }

      const eficlientsecretType = oldSettings.find((s) => s.key === 'eficlientsecret');
      if (eficlientsecretType) {
        setEfiClientsecretType(eficlientsecretType.value);
      }

      const efichavepixType = oldSettings.find((s) => s.key === 'efichavepix');
      if (efichavepixType) {
        setEfiChavepixType(efichavepixType.value);
      }

      const mpaccesstokenType = oldSettings.find((s) => s.key === 'mpaccesstoken');
      if (mpaccesstokenType) {
        setmpaccesstokenType(mpaccesstokenType.value);
      }

      const mppublickeyType = oldSettings.find((s) => s.key === 'mppublickey');
      if (mppublickeyType) {
        setmppublickeyType(mppublickeyType.value);
      }

      const pixdiscountpercentType = oldSettings.find((s) => s.key === 'pixdiscountpercent');
      if (pixdiscountpercentType) {
        setpixdiscountpercentType(pixdiscountpercentType.value);
      }

      const mpwebhooksecretType = oldSettings.find((s) => s.key === 'mpwebhooksecret');
      if (mpwebhooksecretType) {
        setmpwebhooksecretType(mpwebhooksecretType.value);
      }

      const asaastokenType = oldSettings.find((s) => s.key === 'asaastoken');
      if (asaastokenType) {
        setasaastokenType(asaastokenType.value);
      }

    }
  }, [oldSettings])


  useEffect(() => {
    for (const [key, value] of Object.entries(settings)) {
      if (key === "userRating") setUserRating(value);
      if (key === "scheduleType") setScheduleType(value);
      if (key === "chatBotType") setChatBotType(value);
      if (key === "acceptCallWhatsapp") setAcceptCallWhatsapp(value);
      if (key === "userRandom") setUserRandom(value);
      if (key === "sendGreetingMessageOneQueues") setSendGreetingMessageOneQueues(value);
      if (key === "sendSignMessage") setSendSignMessage(value);
      if (key === "sendFarewellWaitingTicket") setSendFarewellWaitingTicket(value);
      if (key === "sendGreetingAccepted") setSendGreetingAccepted(value);
      if (key === "sendQueuePosition") setSendQueuePosition(value);
      if (key === "acceptAudioMessageContact") setAcceptAudioMessageContact(value);
      if (key === "enableLGPD") setEnableLGPD(value);
      if (key === "requiredTag") setRequiredTag(value);
      if (key === "lgpdDeleteMessage") setLGPDDeleteMessage(value)
      if (key === "lgpdHideNumber") setLGPDHideNumber(value);
      if (key === "lgpdConsent") setLGPDConsent(value);
      if (key === "lgpdMessage") setLGPDMessage(value);
      if (key === "sendMsgTransfTicket") setSettingsTransfTicket(value);
      if (key === "lgpdLink") setLGPDLink(value);
      if (key === "DirectTicketsToWallets") setDirectTicketsToWallets(value);
      if (key === "closeTicketOnTransfer") setCloseTicketOnTransfer(value);
      if (key === "transferMessage") setTransferMessage(value);
      if (key === "greetingAcceptedMessage") setGreetingAcceptedMessage(value);
      if (key === "AcceptCallWhatsappMessage") setAcceptCallWhatsappMessage(value);
      if (key === "sendQueuePositionMessage") setSendQueuePositionMessage(value);
      if (key === "showNotificationPending") setShowNotificationPending(value);
      if (key === "twoFactorPolicy") setTwoFactorPolicy(value);
      if (key === "copyContactPrefix") setCopyContactPrefix(value);
    }
  }, [settings]);

  async function handleChangeUserCreation(value) {
    setUserCreation(value);
    setLoadingUserCreation(true);
    await updateUserCreation({
      key: "userCreation",
      value,
    });
    setLoadingUserCreation(false);
  }

  // async function handleDownloadLimit(value) {
  //   setdownloadLimit(value);
  //   setLoadingdownloadLimit(true);
  //   await updatedownloadLimit({
  //     key: "downloadLimit",
  //     value,
  //   });
  //   setLoadingdownloadLimit(false);
  // }

  async function handleChangeEfiClientid(value) {
    setEfiClientidType(value);
    setLoadingEfiClientidType(true);
    await updateeficlientid({
      key: 'eficlientid',
      value,
    });
    toast.success('Operação atualizada com sucesso.');
    setLoadingEfiClientidType(false);
  }

async function handleCopyContactPrefix(value) {
  setCopyContactPrefix(value);
  setLoadingCopyContactPrefix(true);
  await update({
    column: "copyContactPrefix",
    data: value
  });
  setLoadingCopyContactPrefix(false);
}

  async function handleChangeEfiClientsecret(value) {
    setEfiClientsecretType(value);
    setLoadingEfiClientsecretType(true);
    await updateeficlientsecret({
      key: 'eficlientsecret',
      value,
    });
    toast.success('Operação atualizada com sucesso.');
    setLoadingEfiClientsecretType(false);
  }

  async function handleChangeEfiChavepix(value) {
    setEfiChavepixType(value);
    setLoadingEfiChavepixType(true);
    await updateefichavepix({
      key: 'efichavepix',
      value,
    });
    toast.success('Operação atualizada com sucesso.');
    setLoadingEfiChavepixType(false);
  }

  async function handleChangempaccesstoken(value) {
    setmpaccesstokenType(value);
    setLoadingmpaccesstokenType(true);
    await updatempaccesstoken({
      key: 'mpaccesstoken',
      value,
    });
    toast.success('Operação atualizada com sucesso.');
    setLoadingmpaccesstokenType(false);
  }

  async function handleChangemppublickey(value) {
    setmppublickeyType(value);
    setLoadingmppublickeyType(true);
    await updatempwebhooksecret({
      key: 'mppublickey',
      value,
    });
    toast.success(i18n.t("settings.settings.options.updateSuccess"));
    setLoadingmppublickeyType(false);
  }

  async function handleChangepixdiscountpercent(value) {
    setpixdiscountpercentType(value);
    setLoadingpixdiscountpercentType(true);
    await updatempwebhooksecret({
      key: 'pixdiscountpercent',
      value,
    });
    toast.success(i18n.t("settings.settings.options.updateSuccess"));
    setLoadingpixdiscountpercentType(false);
  }

  async function handleChangempwebhooksecret(value) {
    setmpwebhooksecretType(value);
    setLoadingmpwebhooksecretType(true);
    await updatempwebhooksecret({
      key: 'mpwebhooksecret',
      value,
    });
    toast.success('Operação atualizada com sucesso.');
    setLoadingmpwebhooksecretType(false);
  }

  async function handleChangestripeprivatekey(value) {
    setstripeprivatekeyType(value);
    setLoadingstripeprivatekeyType(true);
    await updatestripeprivatekey({
      key: 'stripeprivatekey',
      value,
    });
    toast.success('Operação atualizada com sucesso.');
    setLoadingstripeprivatekeyType(false);
  }

  async function handleChangeasaastoken(value) {
    setasaastokenType(value);
    setLoadingasaastokenType(true);
    await updateasaastoken({
      key: 'asaastoken',
      value,
    });
    toast.success('Operação atualizada com sucesso.');
    setLoadingasaastokenType(false);
  }

  async function handleChangeUserRating(value) {
    setUserRating(value);
    setLoadingUserRating(true);
    await update({
      column: "userRating",
      data: value
    });
    setLoadingUserRating(false);
  }

  async function handleScheduleType(value) {
    setScheduleType(value);
    setLoadingScheduleType(true);
    await update({
      column: "scheduleType",
      data: value
    });
    setLoadingScheduleType(false);
    if (typeof scheduleTypeChanged === "function") {
      scheduleTypeChanged(value);
    }
  }

  async function handleCopyContactPrefix(value) {
    setCopyContactPrefix(value);
    setLoadingCopyContactPrefix(true);
    await update({
      column: "copyContactPrefix",
      data: value
    });
    setLoadingCopyContactPrefix(false);
  }

  async function handleChatBotType(value) {
    setChatBotType(value);
    await update({
      column: "chatBotType",
      data: value
    });
    if (typeof scheduleTypeChanged === "function") {
      setChatBotType(value);
    }
  }

  async function handleLGPDMessage(value) {
    setLGPDMessage(value);
    setLoadingLGPDMessage(true);
    await update({
      column: "lgpdMessage",
      data: value
    });
    setLoadingLGPDMessage(false);
  }

  async function handletransferMessage(value) {
    setTransferMessage(value);
    setLoadingTransferMessage(true);
    await update({
      column: "transferMessage",
      data: value
    });
    setLoadingTransferMessage(false);
  }

  async function handleGreetingAcceptedMessage(value) {
    setGreetingAcceptedMessage(value);
    setLoadingGreetingAcceptedMessage(true);
    await update({
      column: "greetingAcceptedMessage",
      data: value
    });
    setLoadingGreetingAcceptedMessage(false);
  }

  async function handleAcceptCallWhatsappMessage(value) {
    setAcceptCallWhatsappMessage(value);
    setLoadingAcceptCallWhatsappMessage(true);
    await update({
      column: "AcceptCallWhatsappMessage",
      data: value
    });
    setLoadingAcceptCallWhatsappMessage(false);
  }

  async function handlesendQueuePositionMessage(value) {
    setSendQueuePositionMessage(value);
    setLoadingSendQueuePositionMessage(true);
    await update({
      column: "sendQueuePositionMessage",
      data: value
    });
    setLoadingSendQueuePositionMessage(false);
  }

  async function handleShowNotificationPending(value) {
    setShowNotificationPending(value);
    setLoadingShowNotificationPending(true);
    await update({
      column: "showNotificationPending",
      data: value
    });
    setLoadingShowNotificationPending(false);
  }

  async function handleTwoFactorPolicy(value) {
    setTwoFactorPolicy(value);
    setLoadingTwoFactorPolicy(true);
    await update({
      column: "twoFactorPolicy",
      data: value
    });
    setLoadingTwoFactorPolicy(false);
  }

  async function handleLGPDLink(value) {
    setLGPDLink(value);
    setLoadingLGPDLink(true);
    await update({
      column: "lgpdLink",
      data: value
    });
    setLoadingLGPDLink(false);
  }

  async function handleLGPDDeleteMessage(value) {
    setLGPDDeleteMessage(value);
    setLoadingLGPDDeleteMessage(true);
    await update({
      column: "lgpdDeleteMessage",
      data: value
    });
    setLoadingLGPDDeleteMessage(false);
  }

  async function handleLGPDConsent(value) {
    setLGPDConsent(value);
    setLoadingLGPDConsent(true);
    await update({
      column: "lgpdConsent",
      data: value
    });
    setLoadingLGPDConsent(false);
  }

  async function handleLGPDHideNumber(value) {
    setLGPDHideNumber(value);
    setLoadingLGPDHideNumber(true);
    await update({
      column: "lgpdHideNumber",
      data: value
    });
    setLoadingLGPDHideNumber(false);
  }

  async function handleSendGreetingAccepted(value) {
    setSendGreetingAccepted(value);
    setLoadingSendGreetingAccepted(true);
    await update({
      column: "sendGreetingAccepted",
      data: value
    });
    setLoadingSendGreetingAccepted(false);
  }

  async function handleUserRandom(value) {
    setUserRandom(value);
    setLoadingUserRandom(true);
    await update({
      column: "userRandom",
      data: value
    });
    setLoadingUserRandom(false);
  }

  async function handleSettingsTransfTicket(value) {
    setSettingsTransfTicket(value);
    setLoadingSettingsTransfTicket(true);
    await update({
      column: "sendMsgTransfTicket",
      data: value
    });
    setLoadingSettingsTransfTicket(false);
  }

  async function handleAcceptCallWhatsapp(value) {
    setAcceptCallWhatsapp(value);
    setLoadingAcceptCallWhatsapp(true);
    await update({
      column: "acceptCallWhatsapp",
      data: value
    });
    setLoadingAcceptCallWhatsapp(false);
  }

  async function handleSendSignMessage(value) {
    setSendSignMessage(value);
    setLoadingSendSignMessage(true);
    await update({
      column: "sendSignMessage",
      data: value
    });
    localStorage.setItem("sendSignMessage", value === "enabled" ? true : false); //atualiza localstorage para sessão
    setLoadingSendSignMessage(false);
  }

  async function handleSendGreetingMessageOneQueues(value) {
    setSendGreetingMessageOneQueues(value);
    setLoadingSendGreetingMessageOneQueues(true);
    await update({
      column: "sendGreetingMessageOneQueues",
      data: value
    });
    setLoadingSendGreetingMessageOneQueues(false);
  }

  async function handleSendQueuePosition(value) {
    setSendQueuePosition(value);
    setLoadingSendQueuePosition(true);
    await update({
      column: "sendQueuePosition",
      data: value
    });
    setLoadingSendQueuePosition(false);
  }

  async function handleSendFarewellWaitingTicket(value) {
    setSendFarewellWaitingTicket(value);
    setLoadingSendFarewellWaitingTicket(true);
    await update({
      column: "sendFarewellWaitingTicket",
      data: value
    });
    setLoadingSendFarewellWaitingTicket(false);
  }

  async function handleAcceptAudioMessageContact(value) {
    setAcceptAudioMessageContact(value);
    setLoadingAcceptAudioMessageContact(true);
    await update({
      column: "acceptAudioMessageContact",
      data: value
    });
    setLoadingAcceptAudioMessageContact(false);
  }

  async function handleEnableLGPD(value) {
    setEnableLGPD(value);
    setLoadingEnableLGPD(true);
    await update({
      column: "enableLGPD",
      data: value
    });
    setLoadingEnableLGPD(false);
  }

  async function handleRequiredTag(value) {
    setRequiredTag(value);
    setLoadingRequiredTag(true);
    await update({
      column: "requiredTag",
      data: value,
    });
    setLoadingRequiredTag(false);
  }

  async function handleCloseTicketOnTransfer(value) {
    setCloseTicketOnTransfer(value);
    setLoadingCloseTicketOnTransfer(true);
    await update({
      column: "closeTicketOnTransfer",
      data: value,
    });
    setLoadingCloseTicketOnTransfer(false);
  }

  async function handleDirectTicketsToWallets(value) {
    setDirectTicketsToWallets(value);
    setLoadingDirectTicketsToWallets(true);
    await update({
      column: "DirectTicketsToWallets",
      data: value,
    });
    setLoadingDirectTicketsToWallets(false);
  }

  const opt = (k) => i18n.t(`settings.settings.options.${k}`);
  const desc = (k) => i18n.t(`settings.settings.descriptions.${k}`);
  const updating = opt("updating");

  // Array (não Fragment): o Select do MUI só reconhece MenuItem como filho
  // direto — dentro de <></> ele não casa o value e renderiza vazio.
  const enabledDisabledItems = [
    <MenuItem key="disabled" value={"disabled"}>{opt("disabled")}</MenuItem>,
    <MenuItem key="enabled" value={"enabled"}>{opt("enabled")}</MenuItem>,
  ];

  // Toggles da seção "Recursos" — mesma ordem/semântica dos selects antigos.
  // Chaves com valor boolean no backend recebem `bool: true`.
  const featureToggles = [
    { k: "userRandom", value: UserRandom, loading: loadingUserRandom, on: handleUserRandom },
    { k: "acceptCallWhatsapp", value: AcceptCallWhatsapp, loading: loadingAcceptCallWhatsapp, on: handleAcceptCallWhatsapp },
    { k: "sendSignMessage", value: sendSignMessage, loading: loadingSendSignMessage, on: handleSendSignMessage },
    { k: "sendGreetingMessageOneQueues", value: sendGreetingMessageOneQueues, loading: loadingSendGreetingMessageOneQueues, on: handleSendGreetingMessageOneQueues },
    { k: "sendQueuePosition", value: sendQueuePosition, loading: loadingSendQueuePosition, on: handleSendQueuePosition },
    { k: "sendFarewellWaitingTicket", value: sendFarewellWaitingTicket, loading: loadingSendFarewellWaitingTicket, on: handleSendFarewellWaitingTicket },
    { k: "acceptAudioMessageContact", value: acceptAudioMessageContact, loading: loadingAcceptAudioMessageContact, on: handleAcceptAudioMessageContact },
    { k: "enableLGPD", value: enableLGPD, loading: loadingEnableLGPD, on: handleEnableLGPD },
    { k: "requiredTag", value: requiredTag, loading: loadingRequiredTag, on: handleRequiredTag },
    { k: "closeTicketOnTransfer", value: closeTicketOnTransfer, loading: loadingCloseTicketOnTransfer, on: handleCloseTicketOnTransfer, bool: true },
    { k: "showNotificationPending", value: showNotificationPending, loading: loadingShowNotificationPending, on: handleShowNotificationPending, bool: true },
    { k: "DirectTicketsToWallets", value: directTicketsToWallets, loading: loadingDirectTicketsToWallets, on: handleDirectTicketsToWallets, bool: true },
  ];

  const lgpdToggles = [
    { k: "lgpdDeleteMessage", label: i18n.t("settings.settings.LGPD.obfuscateMessageDelete"), value: lgpdDeleteMessage, loading: loadingLGPDDeleteMessage, on: handleLGPDDeleteMessage },
    { k: "lgpdConsent", label: i18n.t("settings.settings.LGPD.alwaysConsent"), value: lgpdConsent, loading: loadingLGPDConsent, on: handleLGPDConsent },
    { k: "lgpdHideNumber", label: i18n.t("settings.settings.LGPD.obfuscatePhoneUser"), value: lgpdHideNumber, loading: loadingLGPDHideNumber, on: handleLGPDHideNumber },
  ];

  const paymentFields = [
    { id: "eficlientid", label: "Efí (GerenciaNet) · Client ID", value: eficlientidType, loading: loadingEfiClientidType, on: handleChangeEfiClientid },
    { id: "eficlientsecret", label: "Efí (GerenciaNet) · Client Secret", value: eficlientsecretType, loading: loadingEfiClientsecretType, on: handleChangeEfiClientsecret },
    { id: "efichavepix", label: "Efí (GerenciaNet) · Chave PIX", value: efichavepixType, loading: loadingEfiChavepixType, on: handleChangeEfiChavepix },
    { id: "mpaccesstoken", label: "Mercado Pago · Access Token", value: mpaccesstokenType, loading: loadingmpaccesstokenType, on: handleChangempaccesstoken },
    { id: "mpwebhooksecret", label: "Mercado Pago · Webhook Secret", value: mpwebhooksecretType, loading: loadingmpwebhooksecretType, on: handleChangempwebhooksecret },
    { id: "mppublickey", label: i18n.t("settings.settings.payments.mpPublicKey"), value: mppublickeyType, loading: loadingmppublickeyType, on: handleChangemppublickey },
    { id: "pixdiscountpercent", label: i18n.t("settings.settings.payments.pixDiscount"), value: pixdiscountpercentType, loading: loadingpixdiscountpercentType, on: handleChangepixdiscountpercent, type: "number", min: 0, max: 100 },
    { id: "stripeprivatekey", label: "Stripe · Private Key", value: stripeprivatekeyType, loading: loadingstripeprivatekeyType, on: handleChangestripeprivatekey },
    { id: "asaastoken", label: "ASAAS · Token", value: asaastokenType, loading: loadingasaastokenType, on: handleChangeasaastoken },
  ];

  return (
    <>
      {/* ---- selects (grid 3 colunas) ---- */}
      <div className={classes.grid3}>
        {isSuper() && (
          <SelectSettingCard
            classes={classes}
            label={opt("creationCompanyUser")}
            value={userCreation}
            onChange={(e) => handleChangeUserCreation(e.target.value)}
            helperText={loadingUserCreation && updating}
          >
            {enabledDisabledItems}
          </SelectSettingCard>
        )}

        <SelectSettingCard
          classes={classes}
          label={opt("evaluations")}
          value={userRating}
          onChange={(e) => handleChangeUserRating(e.target.value)}
          helperText={loadingUserRating && updating}
        >
          {enabledDisabledItems}
        </SelectSettingCard>

        <SelectSettingCard
          classes={classes}
          label={opt("officeScheduling")}
          value={scheduleType}
          onChange={(e) => handleScheduleType(e.target.value)}
          helperText={loadingScheduleType && updating}
        >
          <MenuItem value={"disabled"}>{opt("disabled")}</MenuItem>
          <MenuItem value={"queue"}>{opt("queueManagement")}</MenuItem>
          <MenuItem value={"company"}>{opt("companyManagement")}</MenuItem>
          <MenuItem value={"connection"}>{opt("connectionManagement")}</MenuItem>
        </SelectSettingCard>

        <SelectSettingCard
          classes={classes}
          label={opt("chatBotType")}
          value={chatBotType}
          onChange={(e) => handleChatBotType(e.target.value)}
          helperText={loadingScheduleType && updating}
        >
          <MenuItem value={"text"}>Texto</MenuItem>
        </SelectSettingCard>

        <SelectSettingCard
          classes={classes}
          label={opt("sendGreetingAccepted")}
          value={SendGreetingAccepted}
          onChange={(e) => handleSendGreetingAccepted(e.target.value)}
          helperText={loadingSendGreetingAccepted && updating}
        >
          {enabledDisabledItems}
        </SelectSettingCard>

        <SelectSettingCard
          classes={classes}
          label={opt("sendMsgTransfTicket")}
          value={SettingsTransfTicket}
          onChange={(e) => handleSettingsTransfTicket(e.target.value)}
          helperText={loadingSettingsTransfTicket && updating}
        >
          {enabledDisabledItems}
        </SelectSettingCard>

        <SelectSettingCard
          classes={classes}
          label={opt("twoFactorPolicy")}
          value={twoFactorPolicy}
          onChange={(e) => handleTwoFactorPolicy(e.target.value)}
          helperText={loadingTwoFactorPolicy && updating}
        >
          <MenuItem value="optional">{opt("twoFactorOptional")}</MenuItem>
          <MenuItem value="mandatory">{opt("twoFactorMandatory")}</MenuItem>
          <MenuItem value="disabled">{opt("twoFactorDisabled")}</MenuItem>
        </SelectSettingCard>

        <TextSettingCard
          classes={classes}
          id="copyContactPrefix"
          name="copyContactPrefix"
          label={opt("copyContactPrefix")}
          placeholder={opt("copyContactPrefixPlaceholder")}
          value={copyContactPrefix}
          onChange={(e) => handleCopyContactPrefix(e.target.value)}
          helperText={loadingCopyContactPrefix && updating}
        />
      </div>

      {/* ---- Recursos (toggles, grid 2 colunas) ---- */}
      <SectionLabel classes={classes}>{i18n.t("settings.settings.sections.features")}</SectionLabel>
      <div className={classes.grid2}>
        {featureToggles.map((t) => (
          <ToggleSettingCard
            key={t.k}
            classes={classes}
            label={opt(t.k)}
            desc={desc(t.k)}
            checked={isOn(t.value)}
            loading={t.loading}
            onToggle={(next) => t.on(t.bool ? next : asFlag(next))}
          />
        ))}
      </div>

      {/* ---- LGPD (só com o tratamento habilitado) ---- */}
      {enableLGPD === "enabled" && (
        <>
          <SectionLabel classes={classes}>{i18n.t("settings.settings.LGPD.title")}</SectionLabel>
          <div className={classes.grid2Wide}>
            <TextSettingCard
              classes={classes}
              id="lgpdMessage"
              name="lgpdMessage"
              multiline
              rows={3}
              label={i18n.t("settings.settings.LGPD.welcome")}
              value={lgpdMessage}
              onChange={(e) => handleLGPDMessage(e.target.value)}
              helperText={loadinglgpdMessage && updating}
            />
            <TextSettingCard
              classes={classes}
              id="lgpdLink"
              name="lgpdLink"
              label={i18n.t("settings.settings.LGPD.linkLGPD")}
              value={lgpdLink}
              onChange={(e) => handleLGPDLink(e.target.value)}
              helperText={loadingLGPDLink && updating}
            />
            {lgpdToggles.map((t) => (
              <ToggleSettingCard
                key={t.k}
                classes={classes}
                label={t.label}
                desc={desc(t.k)}
                checked={isOn(t.value)}
                loading={t.loading}
                onToggle={(next) => t.on(asFlag(next))}
              />
            ))}
          </div>
        </>
      )}

      {/* ---- Mensagens personalizadas ---- */}
      <SectionLabel classes={classes}>{i18n.t("settings.settings.sections.messages")}</SectionLabel>
      <div className={classes.grid2Wide}>
        <TextSettingCard
          classes={classes}
          id="transferMessage"
          name="transferMessage"
          multiline
          rows={3}
          label={i18n.t("settings.settings.customMessages.transferMessage")}
          value={transferMessage}
          required={SettingsTransfTicket === "enabled"}
          onChange={(e) => handletransferMessage(e.target.value)}
          helperText={loadingTransferMessage && updating}
        />
        <TextSettingCard
          classes={classes}
          id="greetingAcceptedMessage"
          name="greetingAcceptedMessage"
          multiline
          rows={3}
          label={i18n.t("settings.settings.customMessages.greetingAcceptedMessage")}
          value={greetingAcceptedMessage}
          required={SendGreetingAccepted === "enabled"}
          onChange={(e) => handleGreetingAcceptedMessage(e.target.value)}
          helperText={loadingGreetingAcceptedMessage && updating}
        />
        <TextSettingCard
          classes={classes}
          id="AcceptCallWhatsappMessage"
          name="AcceptCallWhatsappMessage"
          multiline
          rows={3}
          label={i18n.t("settings.settings.customMessages.AcceptCallWhatsappMessage")}
          value={AcceptCallWhatsappMessage}
          required={AcceptCallWhatsapp === "disabled"}
          onChange={(e) => handleAcceptCallWhatsappMessage(e.target.value)}
          helperText={loadingAcceptCallWhatsappMessage && updating}
        />
        <TextSettingCard
          classes={classes}
          id="sendQueuePositionMessage"
          name="sendQueuePositionMessage"
          multiline
          rows={3}
          label={i18n.t("settings.settings.customMessages.sendQueuePositionMessage")}
          value={sendQueuePositionMessage}
          required={sendQueuePosition === "enabled"}
          onChange={(e) => handlesendQueuePositionMessage(e.target.value)}
          helperText={loadingSendQueuePositionMessage && updating}
        />
      </div>

      {/* ---- Pagamentos (somente super) ---- */}
      {isSuper() && (
        <>
          <SectionLabel classes={classes}>{i18n.t("settings.settings.sections.payments")}</SectionLabel>
          <div className={classes.grid2Wide}>
            {paymentFields.map((f) => (
              <TextSettingCard
                key={f.id}
                classes={classes}
                id={f.id}
                name={f.id}
                label={f.label}
                value={f.value}
                onChange={(e) => f.on(e.target.value)}
                helperText={f.loading && "Atualizando..."}
                {...(f.type
                  ? { type: f.type, inputProps: { "aria-label": f.label, min: f.min, max: f.max } }
                  : {})}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
}
